import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

type Props = {
  title: string;
  isLarge?: boolean;
  isMobile: boolean;
  isTablet: boolean;
  children: ReactNode;
};

const HeroSection = ({
  title,
  isMobile,
  isTablet,
  isLarge,
  children,
}: Props) => {
  return (
    <Wrapper>
      <Container $isMobile={isMobile} $isLarge={isLarge}>
        <Title $isMobile={isMobile} $isTablet={isTablet}>
          {title}
        </Title>

        <FirstBubble
          $usePct
          $left={-7}
          $top={32}
          $height={273}
          $width={273}
          $isMobile={isMobile}
          $isTablet={isTablet}
        >
          <BlueBubbleCircle />
        </FirstBubble>
        <SecondBubble
          $usePct
          $left={15}
          $top={60}
          $height={73}
          $width={73}
          $isTablet={isTablet}
          $isMobile={isMobile}
        >
          <BlueBubbleCircle />
        </SecondBubble>
        <ThirdBubble
          $usePct
          $right={-2}
          $top={28}
          $height={73}
          $width={73}
          $isMobile={isMobile}
          $isTablet={isTablet}
        >
          <BlueBubbleCircle />
        </ThirdBubble>
        <FourthBubble
          $usePct
          $right={14}
          $top={74}
          $height={138}
          $width={138}
          $isMobile={isMobile}
          $isTablet={isTablet}
        >
          <BlueBubbleCircle />
        </FourthBubble>
      </Container>
      {children}
    </Wrapper>
  );
};

const BubbleCircle = styled.div`
  background: white;
  width: 100%;
  height: 100%;
  opacity: 0.15;
`;

const BubbleCircleContainer = styled.div<{
  $width: number;
  $height: number;
  $bottom?: number;
  $right?: number;
  $top?: number;
  $left?: number;
  $opacity?: number;
  $usePct?: boolean;
}>`
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  bottom: ${({ $bottom, $usePct }) =>
    $usePct ? `${$bottom}%` : `${$bottom}px`};
  top: ${({ $top, $usePct }) => ($usePct ? `${$top}%` : `${$top}px`)};
  right: ${({ $right, $usePct }) => ($usePct ? `${$right}%` : `${$right}px`)};
  /* stylelint-disable-next-line declaration-block-no-redundant-longhand-properties */
  left: ${({ $left, $usePct }) => ($usePct ? `${$left}%` : `${$left}px`)};
  opacity: ${({ $opacity }) => ($opacity !== undefined ? $opacity : 1)};
  position: absolute;
  border: 1px dashed white;
  border-radius: 50%;
  overflow: hidden;
`;

const Wrapper = styled.div`
  position: relative;
  max-width: 1920px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

const Container = styled.section<{ $isMobile: boolean; $isLarge?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.topicBlue};
  clip-path: ellipse(72% 91% at 50% 9%);
  position: relative;
  padding-bottom: 190px;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      clip-path: ellipse(122% 83% at 50% 9%);
      overflow: hidden;
      padding-bottom: 150px;
    `}

  ${({ $isLarge }) =>
    $isLarge &&
    css`
      height: 250px;
    `}
`;

const Title = styled.h1<{ $isMobile: boolean; $isTablet: boolean }>`
  font-size: 50px;
  line-height: 80px;
  font-weight: 700;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};
  word-break: break-word;
  margin-top: 65px;
  ${({ $isTablet }) =>
    $isTablet &&
    css`
      font-size: 45px;
      line-height: 50px;
    `}
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      font-size: 24px;
      line-height: 45px;
      font-weight: 400;
      margin-top: 40px;
    `}
`;

const BlueBubbleCircle = styled(BubbleCircle)`
  background-color: ${({ theme }) => theme.colors.lightBlue};
`;

const FirstBubble = styled(BubbleCircleContainer)<{
  $isMobile: boolean;
  $isTablet: boolean;
}>`
  ${({ $isTablet }) =>
    $isTablet &&
    css`
      left: -20%;
      top: 50%;
    `}
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      left: -40%;
      top: 60%;
    `}
`;

const SecondBubble = styled(BubbleCircleContainer)<{
  $isMobile: boolean;
  $isTablet: boolean;
}>`
  ${({ $isTablet }) =>
    $isTablet &&
    css`
      display: none;
    `}
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      display: none;
    `}
`;

const ThirdBubble = styled(BubbleCircleContainer)<{
  $isMobile: boolean;
  $isTablet: boolean;
}>`
  ${({ $isTablet }) =>
    $isTablet &&
    css`
      right: -6%;
      top: 20%;
    `}

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      right: -10%;
      top: 30%;
    `}
`;

const FourthBubble = styled(BubbleCircleContainer)<{
  $isMobile: boolean;
  $isTablet: boolean;
}>`
  ${({ $isTablet }) =>
    $isTablet &&
    css`
      right: 6%;
      top: 70%;
    `}

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      display: none;
    `}
`;

export default HeroSection;
